import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { deleteUser } from '../services/user-service';

interface DeleteUserModalProps {
    isOpen: boolean;
    userId: number;
    onClose: () => void;
    onDeleted: () => void;
    onError: (message: string) => void;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
                                                             isOpen,
                                                             userId,
                                                             onClose,
                                                             onDeleted,
                                                             onError,
                                                         }) => {
    const [loading, setLoading] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [localError, setLocalError] = useState('');

    const canDelete = confirmText.trim() === 'DELETE' && !loading;

    const handleDelete = async () => {
        if (confirmText.trim() !== 'DELETE') {
            setLocalError("Type 'DELETE' to confirm.");
            return;
        }

        setLocalError('');
        setLoading(true);

        try {
            const response = await deleteUser(userId);
            if (response.status === 204) {
                onDeleted();
            } else {
                console.error('Unable to delete account', response);
                onError('Unable to delete account. Please try again later.');
            }
        } catch (e) {
            console.error('Unable to delete account', e);
            onError('Unable to delete account. Please try again later.');
        } finally {
            setLoading(false);
            setConfirmText('');
            onClose();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />
            <div className="relative bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
                <Dialog.Title className="text-xl font-semibold mb-2 text-red-600">
                    Permanently delete account
                </Dialog.Title>
                <p className="text-sm text-gray-700 mb-4">
                    This action <strong>cannot</strong> be undone. All of your data will be removed from CrumbCRM.
                </p>

                <label htmlFor="confirmDelete" className="block text-sm font-medium text-gray-700">
                    To confirm, type <span className="font-mono">DELETE</span> below
                </label>
                <input
                    id="confirmDelete"
                    data-testid="confirm-input"
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="mt-1 mb-2 pl-1 pr-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-600 focus:ring-red-600"
                    placeholder="DELETE"
                    autoComplete="off"
                />
                {localError && (
                    <p data-testid="confirm-error" className="text-xs text-red-600 mb-2">{localError}</p>
                )}

                <div className="mt-4 flex justify-end gap-3">
                    <button
                        data-testid="close-btn"
                        onClick={() => {
                            setConfirmText('');
                            onClose();
                            }
                        }
                        className="px-4 py-2 rounded border"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="delete-btn"
                        onClick={handleDelete}
                        className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50"
                        disabled={!canDelete}
                    >
                        {loading ? 'Deleting...' : 'Delete account'}
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default DeleteUserModal;